import { useMemo } from 'react';
import { SecurityRead } from 'api';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useFundOwnershipContext } from 'context/FundOwnershipContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { FundOwnershipTableAlias } from 'pages/CapTable/fund-ownership/utilities/rowConfig';
import { RelatedEntity } from 'pages/ValuationsAllocation/types';
import { useGetCapTableById } from 'services/hooks/capTables';
import {
  UseSharesLedgerSecurityProps,
  UseSharesLedgerSecurityResult,
} from './useSharesLedgerSecurity.props';

const EMPTY_SECURITY = {} as SecurityRead;

const useSharesLedgerSecurity = ({
  cells,
  cell,
  relatedSecurityId,
  isCustomSecurity,
}: UseSharesLedgerSecurityProps): UseSharesLedgerSecurityResult => {
  const [{ captableInfo, companyInfo }] = useStore() as unknown as UseStoreValues;
  const { capTableVersionsByCompany } = useFundOwnershipContext();

  const entityCell = useMemo((): ICell | undefined => (Object.values(cells) as ICell[]).find(
    spreadsheetCell =>
      spreadsheetCell.alias === FundOwnershipTableAlias.ENTITY_REFERENCE
        && spreadsheetCell.columnLegend === cell.columnLegend
  ), [cells, cell.columnLegend]);

  const targetCapTableId = useMemo(() => {
    const loadedCapTableId = captableInfo?.id;
    if (!entityCell?.value || !companyInfo) {
      return loadedCapTableId;
    }

    const entityId = entityCell.value;
    const entityIsParentCompany = entityId === companyInfo.id;
    if (entityIsParentCompany) {
      return loadedCapTableId;
    }

    const entityName = companyInfo.child_companies?.find(
      (child: RelatedEntity) => Number(child.company_id) === Number(entityId)
    )?.company_name;

    if (!entityName) {
      return loadedCapTableId;
    }

    const primaryCapTableVersion = capTableVersionsByCompany[entityName]?.find(version => version.is_primary);
    return primaryCapTableVersion?.id ?? loadedCapTableId;
  }, [capTableVersionsByCompany, captableInfo?.id, companyInfo, entityCell]);

  const shouldFetchRelatedCapTable =
    !isCustomSecurity && targetCapTableId != null && targetCapTableId !== captableInfo?.id;

  const { data: relatedCapTable } = useGetCapTableById({
    capTableId: shouldFetchRelatedCapTable ? targetCapTableId : undefined,
    shouldQueryAutomatically: shouldFetchRelatedCapTable,
  });

  const securities = useMemo(() => {
    if (shouldFetchRelatedCapTable) {
      return relatedCapTable?.securities ?? [];
    }
    return captableInfo?.securities ?? [];
  }, [captableInfo?.securities, relatedCapTable?.securities, shouldFetchRelatedCapTable]);

  const currentSecurity = useMemo(() => {
    if (isCustomSecurity || relatedSecurityId == null) {
      return EMPTY_SECURITY;
    }
    return securities.find(security => security.id === relatedSecurityId) ?? EMPTY_SECURITY;
  }, [isCustomSecurity, relatedSecurityId, securities]);

  return { securities, currentSecurity };
};

export default useSharesLedgerSecurity;
